import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, KeyRound, Trash2, UserCog, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Card, EmptyState } from "./fxui";

const ROLE_LABEL = {
  admin: "Admin", ops: "Operations (all)", picker: "Picker", packer: "Packer", loader: "Loader",
  inbound: "Ghana Inbound", delivery: "Delivery", support: "Support",
};

export default function FxStaff() {
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [pwFor, setPwFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/fx/staff"); setStaff(data.staff || []); setRoles(data.roles || []); }
    catch { toast.error("Could not load staff"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const changeRole = async (id, role) => { await api.put(`/fx/staff/${id}`, { role }); load(); };
  const toggleActive = async (s) => {
    try { await api.put(`/fx/staff/${s.id}`, { active: !s.active }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.email}? This removes their login.`)) return;
    try { await api.delete(`/fx/staff/${s.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  return (
    <div data-testid="fx-staff">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
        <p className="text-sm text-[#5C5C5C] max-w-xl">Add workers and give each a role. Everyone signs in at the same <b>/admin/login</b> and sees only their workspace.</p>
        <button onClick={() => setCreating(true)} data-testid="fx-staff-new"
          className="flex items-center gap-2 bg-[#306D29] hover:bg-[#0D530E] text-white px-4 py-2.5 rounded-full text-sm font-semibold"><Plus size={16} /> Add worker</button>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#306D29]" /></div>
        : staff.length === 0 ? <EmptyState>No staff yet. Add your first worker.</EmptyState>
        : (
        <div className="space-y-2">
          {staff.map((s) => (
            <Card key={s.id} className={`flex flex-wrap items-center gap-3 ${s.active ? "" : "opacity-60"}`}>
              <UserCog size={18} className="text-[#306D29]" />
              <div className="flex-1 min-w-[180px]">
                <p className="font-semibold text-[#0D530E]">{s.name} {!s.active && <span className="text-xs text-[#B23B3B]">(disabled)</span>}</p>
                <p className="text-xs text-[#5C5C5C]">{s.email}</p>
              </div>
              <select data-testid={`fx-staff-role-${s.id}`} value={s.role} onChange={(e) => changeRole(s.id, e.target.value)}
                className="bg-white border border-black/10 rounded-full px-3 py-1.5 text-sm">
                {roles.map((r) => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}
              </select>
              <button onClick={() => toggleActive(s)} data-testid={`fx-staff-toggle-${s.id}`}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full ${s.active ? "bg-white border border-black/10 text-[#5C5C5C]" : "bg-[#306D29] text-white"}`}>
                {s.active ? "Disable" : "Enable"}
              </button>
              <button onClick={() => setPwFor(s)} data-testid={`fx-staff-pw-${s.id}`} title="Reset password"
                className="w-8 h-8 rounded-lg flex items-center justify-center text-[#5C5C5C] hover:bg-black/5"><KeyRound size={16} /></button>
              <button onClick={() => remove(s)} data-testid={`fx-staff-del-${s.id}`} title="Delete"
                className="w-8 h-8 rounded-lg flex items-center justify-center text-[#B23B3B] hover:bg-[#B23B3B]/10"><Trash2 size={16} /></button>
            </Card>
          ))}
        </div>
      )}

      {creating && <CreateModal roles={roles} onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />}
      {pwFor && <PasswordModal staff={pwFor} onClose={() => setPwFor(null)} />}
    </div>
  );
}

function CreateModal({ roles, onClose, onDone }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState(roles.find((r) => r !== "admin") || "picker");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try { await api.post("/fx/staff", { email, name, role, password }); toast.success("Worker added"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not create worker"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FBF5DD] rounded-3xl p-7 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="fx-staff-modal">
        <div className="flex justify-between items-center mb-4"><h3 className="font-display text-2xl text-[#0D530E]">Add worker</h3><button onClick={onClose}><X size={20} /></button></div>
        <label className="text-sm font-semibold">Email (their login)</label>
        <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" data-testid="fx-staff-email"
          className="w-full bg-white border border-black/10 rounded-xl px-3 py-2.5 text-sm mt-1 mb-3" />
        <label className="text-sm font-semibold">Name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="fx-staff-name"
          className="w-full bg-white border border-black/10 rounded-xl px-3 py-2.5 text-sm mt-1 mb-3" />
        <label className="text-sm font-semibold">Role</label>
        <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="fx-staff-role-select"
          className="w-full bg-white border border-black/10 rounded-xl px-3 py-2.5 text-sm mt-1 mb-3">
          {roles.map((r) => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}
        </select>
        <label className="text-sm font-semibold">Temporary password</label>
        <input value={password} onChange={(e) => setPassword(e.target.value)} data-testid="fx-staff-password"
          className="w-full bg-white border border-black/10 rounded-xl px-3 py-2.5 text-sm mt-1 mb-5" />
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-full border border-black/10 text-sm">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="fx-staff-save" className="flex-1 py-2.5 rounded-full bg-[#306D29] text-white text-sm font-semibold disabled:opacity-60">Add worker</button>
        </div>
      </div>
    </div>
  );
}

function PasswordModal({ staff, onClose }) {
  const [password, setPassword] = useState("");
  const save = async () => {
    try { await api.post(`/fx/staff/${staff.id}/password`, { password }); toast.success("Password reset"); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not reset"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FBF5DD] rounded-3xl p-7 max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="fx-staff-pw-modal">
        <h3 className="font-display text-2xl text-[#0D530E] mb-1">Reset password</h3>
        <p className="text-sm text-[#5C5C5C] mb-4">{staff.email}</p>
        <input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password" data-testid="fx-staff-newpw"
          className="w-full bg-white border border-black/10 rounded-xl px-3 py-2.5 text-sm mb-4" />
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-full border border-black/10 text-sm">Cancel</button>
          <button onClick={save} data-testid="fx-staff-pw-save" className="flex-1 py-2.5 rounded-full bg-[#306D29] text-white text-sm font-semibold">Reset</button>
        </div>
      </div>
    </div>
  );
}
